import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ClaimExpenseInput, UpdateClaimExpenseInput } from "@upclaim/sdk/src/data-contracts";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

function useInvalidateClaim(claimId: string) {
  const queryClient = useQueryClient();
  return async (success: boolean) => {
    if (success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    }
  };
}

/** Mirrors UpsertExpense.addExpense (Vue) — POST /admin/claims/{id}/expense. */
export function useAddClaimExpense(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: (payload: ClaimExpenseInput) =>
      notify.promise(adminAPI.addClaimExpense(claimId, payload), {
        loading: "Enregistrement de la dépense…",
        success: "Dépense enregistrée avec succès",
      }),
    onSuccess: ({ success }) => invalidate(success),
  });
}

/** Mirrors UpsertExpense.updateExpense (Vue) — PATCH /admin/claims/{claimId}/expenses/{expenseId}. */
export function useUpdateExpense(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: ({ expenseId, payload }: { expenseId: number; payload: UpdateClaimExpenseInput }) =>
      notify.promise(adminAPI.updateExpense(claimId, String(expenseId), payload), {
        loading: "Enregistrement de la dépense…",
        success: "Dépense enregistrée avec succès",
      }),
    onSuccess: ({ success }) => invalidate(success),
  });
}

/** Mirrors UpsertExpense.deleteExpense (Vue) — DELETE /admin/claims/{claimId}/expenses/{expenseId}. */
export function useDeleteExpense(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: (expenseId: number) =>
      notify.promise(adminAPI.deleteExpense(claimId, expenseId), {
        loading: "Suppression de la dépense…",
        success: "Préjudice supprimé avec succès",
      }),
    onSuccess: ({ success }) => invalidate(success),
  });
}

export type ReimbursalStatus = "PENDING" | "REIMBURSED" | "PARTIAL" | "REJECTED";

/** Mirrors Expenses.saveReimbursal (Vue) — PATCH /admin/claims/{claimId}/expenses/{expenseId}/reimbursal. */
export function useUpdateExpenseReimbursal(claimId: string) {
  const invalidate = useInvalidateClaim(claimId);

  return useMutation({
    mutationFn: ({
      expenseId,
      status,
      reimbursedAmountCents,
    }: {
      expenseId: number;
      status: ReimbursalStatus;
      reimbursedAmountCents?: number;
    }) =>
      notify.promise(
        adminAPI.updateExpenseReimbursal(claimId, expenseId, {
          status,
          ...(status === "REIMBURSED" || status === "PARTIAL" ? { reimbursedAmountCents } : {}),
        }),
        { loading: "Mise à jour du remboursement…", success: "Remboursement mis à jour" },
      ),
    onSuccess: ({ success }) => invalidate(success),
  });
}
